import { useEffect, useMemo, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";

import type { ICountryObject } from "app/services/CountryService";
import { getCountries } from "app/components/filters-bar/utils";
import { DEFAULT_LANGUAGE } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/LanguageDropdown/constants";
import {
    JOB_TITLE_OPTIONS,
    ROLES_OTHER_OPTION,
} from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/constants/jobTitleOptions";
import { aiAgentKnowledgeBaseQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/queries/queryKeyFactory";
import { useGetCustomization } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/queries/useGetCustomization";
import { useUpdateCustomization } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/queries/useUpdateCustomization";
import type {
    KnowledgeBase,
    KnowledgeBasePayload,
} from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/types";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

export { ROLES_OTHER_OPTION };

export const useKnowledgeBase = (): {
    value: KnowledgeBase;
    onChange: <K extends keyof KnowledgeBase>(key: K, value: KnowledgeBase[K]) => void;
    onSubmit: () => void;
    resetValues: () => void;
} => {
    const queryClient = useQueryClient();
    const defaultCountry = useMemo(() => {
        const countries = getCountries() as ICountryObject[];
        return Array.isArray(countries) && countries[0]?.id !== undefined ? countries[0].id : WORLDWIDE_COUNTRY_ID;
    }, []);

    const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBase>({
        productAndOfferings: [],
        language: DEFAULT_LANGUAGE,
        targetMarketCountry: defaultCountry,
        additionalNotes: "",
        roles: [],
        role: undefined,
        roleOption: undefined,
        targetPersonas: [],
        targetCompanies: [],
        files: [],
    });

    const stateBeforeSaveRef = useRef<KnowledgeBase | null>(null);

    const { data } = useGetCustomization();
    const customizationData = data?.model;
    const files = useMemo(() => data?.files ?? [], [data?.files]);

    const { mutate } = useUpdateCustomization();

    useEffect(() => {
        if (customizationData) {
            setKnowledgeBase((prev) => {
                let next: KnowledgeBase = {
                    ...prev,
                    ...customizationData,
                    language: customizationData.language ?? DEFAULT_LANGUAGE,
                    targetMarketCountry: customizationData.targetMarketCountry || defaultCountry,
                    roleOption: prev.roles.length
                        ? prev.roles.find((role) => role === prev.roleOption)
                            ? prev.roleOption
                            : ROLES_OTHER_OPTION
                        : customizationData.role,
                };

                const roles = Array.from(
                    new Set([...(next.role ? [next.role] : []), ...JOB_TITLE_OPTIONS]),
                );
                next = {
                    ...next,
                    roles,
                    roleOption: JOB_TITLE_OPTIONS.find((role) => role === next.roleOption)
                        ? next.roleOption
                        : ROLES_OTHER_OPTION,
                };

                stateBeforeSaveRef.current = next;

                return next;
            });
        }
    }, [customizationData, defaultCountry]);

    useEffect(() => {
        if (files) {
            setKnowledgeBase((prev) => ({ ...prev, files }));
            if (stateBeforeSaveRef.current) {
                stateBeforeSaveRef.current = { ...stateBeforeSaveRef.current, files };
            }
        }
    }, [files]);

    const handleSubmit = () => {
        const {
            productAndOfferings,
            newProductOffering,
            language,
            targetPersonas,
            targetCompanies,
            role,
            additionalNotes,
            targetMarketCountry,
        } = knowledgeBase;

        const payload: KnowledgeBasePayload = {
            productAndOfferings: [...productAndOfferings, ...(newProductOffering ? [newProductOffering] : [])],
            language: language === DEFAULT_LANGUAGE ? null : language,
            targetPersonas,
            targetCompanies,
            role,
            additionalNotes,
            targetMarketCountry,
        };

        mutate(payload);
    };

    const resetValues = () => {
        if (stateBeforeSaveRef.current && !isEqual(knowledgeBase, stateBeforeSaveRef.current)) {
            queryClient.setQueryData(
                aiAgentKnowledgeBaseQueryKeys.customization(),
                (oldData: KnowledgeBasePayload) => ({
                    ...oldData,
                    model: stateBeforeSaveRef.current,
                }),
            );
        }
    };

    return {
        value: knowledgeBase,
        onChange: (key, value) => setKnowledgeBase((prevState) => ({ ...prevState, [key]: value })),
        onSubmit: handleSubmit,
        resetValues,
    };
};
